import type { BusinessData } from '@/api/generated/models/BusinessData'
import type { PerformanceGroupData } from '@/api/generated/models/PerformanceGroupData'
import type { PerformanceCategoryData } from '@/api/generated/models/PerformanceCategoryData'
import type { PerformanceClassData } from '@/api/generated/models/PerformanceClassData'
import type { PerformanceData } from '@/api/generated/models/PerformanceData'
import type { PerformanceTreeNode } from '../types'

/**
 * 타입별 표시명 매핑
 */
export const getPerformanceTypeLabel = (
  type: 'business' | 'performance_group' | 'performance_category' | 'performance_class' | 'performance',
  linkType?: string,
): string => {
  switch (type) {
    case 'business':
      return '사업'
    case 'performance_group':
      return '지표구분'
    case 'performance_category':
      return '하위분류'
    case 'performance_class':
      return '성과지표'
    case 'performance':
      switch (linkType) {
        case 'direct':
          return '세부지표'
        case 'task':
          return '연결세부지표'
        default:
          return '알 수 없음'
      }
    default:
      return '알 수 없음'
  }
}

/**
 * 달성률 계산
 */
export const calculateAchievementRate = (target: number, actual: number): number => {
  if (target === 0) return actual > 0 ? 100 : 0
  return Math.round((actual / target) * 100 * 10) / 10
}

/**
 * API 응답 데이터를 트리 노드로 변환
 * New structure: Business → PerformanceGroup → PerformanceCategory (recursive) → PerformanceClass → Performance
 */
export const transformPerformanceData = (
  response: BusinessData[],
): PerformanceTreeNode[] => {
  if (!Array.isArray(response)) {
    console.warn('Expected BusinessData array, received:', response)
    return []
  }

  return response.map(business => transformBusinessDataToTreeNode(business))
}

/**
 * BusinessData를 트리 노드로 변환
 */
const transformBusinessDataToTreeNode = (business: BusinessData): PerformanceTreeNode => {
  const businessNode: PerformanceTreeNode = {
    id: business.id,
    name: business.name,
    type: 'business',
    businessId: business.id,
    targetValue: null,
    actualValue: null,
    achievementRate: null,
    assignee: null,
    department: null,
    children: [],
    orgHierarchy: [business.name],
    workspace_id: business.workspace_id,
    workspace_name: business.workspace_name,
  }

  // Performance Groups 처리
  if (business.performance_groups && business.performance_groups.length > 0) {
    const { children, aggregatedTarget, aggregatedActual } = transformPerformanceGroups(
      business.performance_groups,
      [business.name],
      business.id,
    )
    businessNode.children = children
  }

  return businessNode
}

/**
 * Performance Groups를 트리 노드로 변환
 */
const transformPerformanceGroups = (
  groups: PerformanceGroupData[],
  parentHierarchy: string[],
  businessId: string,
): {
  children: PerformanceTreeNode[],
  aggregatedTarget: number,
  aggregatedActual: number
} => {
  const children: PerformanceTreeNode[] = []
  let aggregatedTarget = 0
  let aggregatedActual = 0

  groups.forEach(group => {
    const hierarchy = [...parentHierarchy, group.name]

    const groupNode: PerformanceTreeNode = {
      id: group.id,
      name: group.name,
      type: 'performance_group',
      businessId: businessId,
      targetValue: null,
      actualValue: null,
      achievementRate: group.figure_percentage,
      assignee: null,
      department: null,
      children: [],
      orgHierarchy: hierarchy,
      business_id: group.business_id,
      business_name: group.business_name,
      workspace_id: group.workspace_id,
      workspace_name: group.workspace_name,
    }

    // Performance Categories 처리
    if (group.performance_categories && group.performance_categories.length > 0) {
      const { children: subChildren, aggregatedTarget: subTarget, aggregatedActual: subActual } =
        transformPerformanceCategories(group.performance_categories, hierarchy, businessId)

      groupNode.children = subChildren
      groupNode.targetValue = subTarget
      groupNode.actualValue = subActual
      if (groupNode.achievementRate === null && subTarget > 0) {
        groupNode.achievementRate = calculateAchievementRate(subTarget, subActual)
      }

      aggregatedTarget += subTarget
      aggregatedActual += subActual
    }

    children.push(groupNode)
  })

  return { children, aggregatedTarget, aggregatedActual }
}

/**
 * Performance Categories를 트리 노드로 변환 (재귀적)
 */
const transformPerformanceCategories = (
  categories: PerformanceCategoryData[],
  parentHierarchy: string[],
  businessId: string,
): {
  children: PerformanceTreeNode[],
  aggregatedTarget: number,
  aggregatedActual: number
} => {
  const children: PerformanceTreeNode[] = []
  let aggregatedTarget = 0
  let aggregatedActual = 0

  categories.forEach(category => {
    const hierarchy = [...parentHierarchy, category.name]

    const categoryNode: PerformanceTreeNode = {
      id: category.id,
      name: category.name,
      type: 'performance_category',
      businessId: businessId,
      targetValue: null,
      actualValue: null,
      achievementRate: category.figure_percentage,
      assignee: null,
      department: null,
      children: [],
      orgHierarchy: hierarchy,
      groupId: category.group_id || (category as any).groud_id, // Handle typo in API response
      parentId: category.parent_id,
      weight: category.weight,
      manual_weight: category.manual_weight,
    }

    let categoryTarget = 0
    let categoryActual = 0

    // Performance Classes 처리
    if (category.performance_classes && category.performance_classes.length > 0) {
      const { children: classChildren, aggregatedTarget: classTarget, aggregatedActual: classActual } =
        transformPerformanceClasses(category.performance_classes, hierarchy, businessId)

      categoryNode.children.push(...classChildren)
      categoryTarget += classTarget
      categoryActual += classActual
    }

    // 하위 Categories 처리 (재귀적)
    if (category.performance_categories && category.performance_categories.length > 0) {
      const { children: subChildren, aggregatedTarget: subTarget, aggregatedActual: subActual } =
        transformPerformanceCategories(category.performance_categories, hierarchy, businessId)

      categoryNode.children.push(...subChildren)
      categoryTarget += subTarget
      categoryActual += subActual
    }

    categoryNode.targetValue = categoryTarget
    categoryNode.actualValue = categoryActual
    if (categoryNode.achievementRate === null && categoryTarget > 0) {
      categoryNode.achievementRate = calculateAchievementRate(categoryTarget, categoryActual)
    }

    aggregatedTarget += categoryTarget
    aggregatedActual += categoryActual
    children.push(categoryNode)
  })

  return { children, aggregatedTarget, aggregatedActual }
}

/**
 * Performance Classes를 트리 노드로 변환
 */
const transformPerformanceClasses = (
  classes: PerformanceClassData[],
  parentHierarchy: string[],
  businessId: string,
): {
  children: PerformanceTreeNode[],
  aggregatedTarget: number,
  aggregatedActual: number
} => {
  const children: PerformanceTreeNode[] = []
  let aggregatedTarget = 0
  let aggregatedActual = 0

  classes.forEach(perfClass => {
    const hierarchy = [...parentHierarchy, perfClass.name]

    const classNode: PerformanceTreeNode = {
      id: perfClass.id,
      name: perfClass.name,
      type: 'performance_class',
      businessId: businessId,
      targetValue: perfClass.target_figure,
      actualValue: perfClass.done_figure,
      achievementRate: perfClass.figure_percentage,
      assignee: null,
      department: null,
      children: [],
      orgHierarchy: hierarchy,
      weight: perfClass.weight,
      manual_weight: perfClass.manual_weight,
      target_figure_formula: perfClass.target_figure_formula,
      done_figure_formula: perfClass.done_figure_formula,
      performance_category_id: perfClass.performance_category_id,
    }

    const classTarget = perfClass.target_figure
    const classActual = perfClass.done_figure

    // Performances 처리
    if (perfClass.performances && perfClass.performances.length > 0) {
      const { children: perfChildren, aggregatedTarget: perfTarget, aggregatedActual: perfActual } =
        transformPerformances(perfClass.performances, hierarchy, businessId)

      classNode.children = perfChildren
      // Performance Class의 타겟/실제값은 공식에 의해 계산되므로 하위 성과들의 합계로 덮어쓰지 않음
      // 하지만 하위 성과들도 집계에 반영
      aggregatedTarget += classTarget
      aggregatedActual += classActual
    } else {
      aggregatedTarget += classTarget
      aggregatedActual += classActual
    }

    children.push(classNode)
  })

  return { children, aggregatedTarget, aggregatedActual }
}

/**
 * Performances를 트리 노드로 변환
 */
const transformPerformances = (
  performances: PerformanceData[],
  parentHierarchy: string[],
  businessId: string,
): {
  children: PerformanceTreeNode[],
  aggregatedTarget: number,
  aggregatedActual: number
} => {
  const children: PerformanceTreeNode[] = []
  let aggregatedTarget = 0
  let aggregatedActual = 0

  performances.forEach(performance => {
    const hierarchy = [...parentHierarchy, performance.name]

    const performanceNode: PerformanceTreeNode = {
      id: performance.id,
      name: performance.name,
      type: 'performance',
      businessId: businessId,
      targetValue: performance.target_figure,
      actualValue: performance.done_figure,
      achievementRate: performance.figure_percentage,
      assignee: performance.assignee ? {
        id: performance.assignee.id,
        name: performance.assignee.name,
        email: performance.assignee.email,
        phone: performance.assignee.phone,
        account_id: performance.assignee.account_id,
      } : null,
      department: performance.department_id ? {
        id: performance.department_id,
        name: performance.department_name,
      } : null,
      children: [],
      orgHierarchy: hierarchy,
      linkType: performance.link_type as 'direct' | 'task',
      sort_key: performance.sort_key,
      formular_key: performance.formular_key,
      unit: performance.unit,
      status: performance.status,
      task_id: performance.task_id,
      map_id: performance.map_id,
    }

    aggregatedTarget += performance.target_figure
    aggregatedActual += performance.done_figure
    children.push(performanceNode)
  })

  return { children, aggregatedTarget, aggregatedActual }
}

